import React, { useState, useRef } from 'react';
import { api } from '../services/api';

interface BulkUploadProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  onSuccess: () => void;
}

const BulkUploadDebug: React.FC<BulkUploadProps> = ({ isOpen, onClose, clientId, onSuccess }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile)) {
      setFile(droppedFile);
      setError('');
      setDebugInfo(`File selected: ${droppedFile.name}, Size: ${droppedFile.size} bytes, Type: ${droppedFile.type}`);
    } else {
      setError('Please select a valid CSV or Excel file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && isValidFile(selectedFile)) {
      setFile(selectedFile);
      setError('');
      setDebugInfo(`File selected: ${selectedFile.name}, Size: ${selectedFile.size} bytes, Type: ${selectedFile.type}`);
    } else {
      setError('Please select a valid CSV or Excel file');
    }
  };

  const isValidFile = (file: File) => {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    return validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError('');
    setDebugInfo(`Starting upload for client ID: ${clientId}`);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientId', clientId);

      setDebugInfo(prev => prev + '\nSending request to /devices/bulk-upload...');

      const response = await api.post('/devices/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setDebugInfo(prev => prev + `\nResponse received: ${JSON.stringify(response.data)}`);

      onSuccess();
      onClose();
      setFile(null);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Upload failed';
      setError(errorMsg);
      setDebugInfo(prev => prev + `\nError: ${errorMsg}\nStatus: ${err.response?.status}\nFull error: ${JSON.stringify(err.response?.data)}`);
      console.error('Bulk upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "fleet,serial_number,imei_number,iccid_number,sim_provider,sim_text_number,vehicle_id,application,model_version,device_type,hex_value,device_manufacturer,sim_data_plan\n" +
                      "Fleet A,SN001,123456789012345,12345678901234567890,Vodafone,021234567,VH001,MyAsset,v1.0,Tracker,0x01,Optitrac,5GB\n" +
                      "Fleet B,SN002,123456789012346,12345678901234567891,Spark,021234568,VH002,365Trac,v2.0,Tracker,0x02,Optitrac,10GB";
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'device_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Bulk Upload Devices (Debug)</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
        </div>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragOver ? '#007bff' : '#ddd'}`,
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: isDragOver ? '#f8f9ff' : '#fafafa',
            marginBottom: '20px',
            cursor: 'pointer'
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📤</div>
          <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
            {file ? file.name : 'Drag and drop your file here'}
          </p>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            or click to select a CSV or Excel file
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {debugInfo && (
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '4px', 
            marginBottom: '16px', 
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            <strong>Debug Info:</strong><br />
            {debugInfo}
          </div>
        )}

        {error && (
          <div style={{ color: '#dc3545', backgroundColor: '#f8d7da', padding: '8px 12px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <button
            onClick={downloadTemplate}
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📥 Download Template
          </button>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: file && !uploading ? '#007bff' : '#ccc',
              color: 'white',
              cursor: file && !uploading ? 'pointer' : 'not-allowed'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadDebug;
